import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { storage } from '../storage/localStorage';
import type { ApiError } from '../../types/api.types';

interface ApiErrorResponse {
  message?: string;
  detail?: string | ValidationError[];
  code?: string;
}

interface ValidationError {
  type: string;
  loc: string[];
  msg: string;
  input?: any;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add access code header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessCode = storage.getAccessCode();
    if (accessCode && config.headers) {
      config.headers['X-Access-Code'] = accessCode;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      status: error.response?.status,
    };

    if (error.response) {
      // Server responded with error
      const data = error.response.data as ApiErrorResponse;

      // Handle FastAPI validation errors (422)
      if (error.response.status === 422 && Array.isArray(data?.detail)) {
        const validationErrors = data.detail as ValidationError[];
        apiError.message = validationErrors.map(e => `${e.loc.join('.')}: ${e.msg}`).join('; ');
      } else {
        apiError.message = data?.message || (typeof data?.detail === 'string' ? data.detail : 'Server error occurred');
      }

      apiError.code = data?.code;
      apiError.status = error.response.status;

      // Handle 401 unauthorized - clear access code
      if (error.response.status === 401) {
        storage.removeAccessCode();
        apiError.message = 'Invalid or expired access code';
      }
    } else if (error.request) {
      // Request made but no response
      apiError.message = 'Unable to connect to server. Please check your connection.';
    } else {
      // Error in request setup
      apiError.message = error.message || 'Request failed';
    }

    return Promise.reject(apiError);
  }
);

export default apiClient;
